import type { FC, PointerEvent, ReactNode } from 'react'
import type { TeamMember } from '@/types'

import { useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/motion/reveal'
import { ToneSection } from '@/components/generals/tone'

import {
    AnimatePresence,
    motion,
    useMotionValue,
    useSpring
} from 'motion/react'

interface TeamListProps {
    team: TeamMember[]
}

// Names set large; hovering one brings that person's portrait to the cursor.
export const TeamList: FC<TeamListProps> = ({ team }): ReactNode => {
    const area = useRef<HTMLDivElement>(null)
    const [hovered, setHovered] = useState<TeamMember | null>(null)
    const x = useMotionValue(0)
    const y = useMotionValue(0)
    const springX = useSpring(x, { stiffness: 200, damping: 24 })
    const springY = useSpring(y, { stiffness: 200, damping: 24 })

    const onMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!area.current || event.pointerType !== 'mouse') return

        const rect = area.current.getBoundingClientRect()

        x.set(event.clientX - rect.left)
        y.set(event.clientY - rect.top)
    }

    return (
        <ToneSection tone='void' id='team' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <Reveal className='mb-14 grid gap-6 md:grid-cols-12 lg:mb-20'>
                <span className='t-label text-tone-muted md:col-span-3'>
                    Leadership
                </span>
                <h2 className='font-heading t-h1 font-semibold uppercase wdth-75 md:col-span-9'>
                    Our management team
                </h2>
            </Reveal>

            {team.length === 0 ? (
                <p className='border-y border-tone-line py-10 text-tone-muted'>
                    The team list is being updated. Please check back shortly.
                </p>
            ) : (
                <div ref={area} className='relative' onPointerMove={onMove} onMouseLeave={() => setHovered(null)}>
                    <ul className='border-b border-tone-line'>
                        {team.map((member, index) => (
                            <li
                                key={member.id}
                                className='group grid grid-cols-[4.5rem_1fr] items-center gap-5 border-t border-tone-line py-5 md:grid-cols-12 md:py-7'
                                onMouseEnter={() => setHovered(member)}
                            >
                                <span className='relative block aspect-square w-[4.5rem] overflow-hidden bg-tone-surface md:hidden'>
                                    {member.photo && (
                                        <img src={member.photo} alt={member.photo_alt ?? member.name} loading='lazy' className='size-full object-cover grayscale' />
                                    )}
                                </span>

                                <span className='hidden t-label text-tone-muted md:col-span-1 md:block'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <span className='flex flex-col gap-2 md:col-span-11 md:grid md:grid-cols-11 md:items-center md:gap-6'>
                                    <span
                                        data-mdlfy-cms={`team_members:${member.id}:name`}
                                        className={cn(
                                            'font-heading text-[clamp(2rem,5.6vw,5.5rem)] font-semibold uppercase leading-[0.86] tracking-[-0.04em] md:col-span-7',
                                            'transition-[font-variation-settings,opacity] duration-700 ease-out-expo [font-variation-settings:"wdth"_75] group-hover:[font-variation-settings:"wdth"_100]',
                                            hovered && hovered.id !== member.id && 'md:opacity-30'
                                        )}
                                    >
                                        {member.name}
                                    </span>

                                    <span className='flex items-center justify-between gap-4 md:col-span-4'>
                                        {member.role && (
                                            <span data-mdlfy-cms={`team_members:${member.id}:role`} className='t-label text-tone-accent'>
                                                {member.role}
                                            </span>
                                        )}
                                        {member.linkedin_url && (
                                            <a
                                                href={member.linkedin_url}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                aria-label={`${member.name} on LinkedIn`}
                                                className='flex size-11 shrink-0 items-center justify-center border border-tone-line transition-colors duration-500 hover:bg-tone hover:text-tone-bg'
                                            >
                                                <ArrowUpRight className='size-4' aria-hidden />
                                            </a>
                                        )}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>

                    <motion.div
                        aria-hidden
                        className='pointer-events-none absolute left-0 top-0 z-10 hidden md:block'
                        style={{ x: springX, y: springY }}
                    >
                        <AnimatePresence>
                            {hovered?.photo && (
                                <motion.div
                                    key={hovered.id}
                                    className='relative ml-8 aspect-[4/5] w-56 -translate-y-1/2 overflow-hidden bg-tone-surface lg:w-64'
                                    initial={{ opacity: 0, scale: 0.8, clipPath: 'inset(100% 0% 0% 0%)' }}
                                    animate={{ opacity: 1, scale: 1, clipPath: 'inset(0% 0% 0% 0%)' }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <img src={hovered.photo} alt='' className='size-full object-cover grayscale contrast-110' />
                                    <span className='absolute inset-0 bg-violet mix-blend-color opacity-60' />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>
            )}
        </ToneSection>
    )
}
